import { useEffect } from 'react';
import { Navigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { authPaths, globalPaths } from 'constants/routePaths';
import { UserRoles } from 'constants/enums/roles';
import { doEmployeeMe } from 'store/slices/employeeSlice';
import { SliceStatus } from 'constants/enums/slices';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: UserRoles[];
  requiresSupervisor?: boolean;
}
const RoleGuard = ({
  children,
  allowedRoles,
  requiresSupervisor = false,
}: RoleGuardProps) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { employee, employeeSliceStatus } = useAppSelector(
    (state) => state.employee
  );

  useEffect(() => {
    if (
      user &&
      requiresSupervisor &&
      (employeeSliceStatus === SliceStatus.Empty ||
        (employeeSliceStatus !== SliceStatus.Updating &&
          employeeSliceStatus !== SliceStatus.Error &&
          employee._id !== user._id))
    ) {
      dispatch(doEmployeeMe());
    }
  }, [dispatch, employee._id, employeeSliceStatus, requiresSupervisor, user]);

  if (!user || !user.token) {
    return <Navigate to={`${authPaths.basePath}/${authPaths.login}`} replace />;
  }
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={`${globalPaths.notFoundPage}`} replace />;
  }

  const hasFreshEmployeeData = employee._id === user._id;
  const effectiveIsSupervisor = hasFreshEmployeeData
    ? Boolean(employee.isSupervisor)
    : Boolean(user.isSupervisor);

  if (
    requiresSupervisor &&
    !hasFreshEmployeeData &&
    employeeSliceStatus !== SliceStatus.Error
  ) {
    return null;
  }

  if (requiresSupervisor && !effectiveIsSupervisor) {
    return <Navigate to={`${globalPaths.notFoundPage}`} replace />;
  }
  return children;
};

export default RoleGuard;
